import React, { useEffect, useState } from "react";
import { Loader2, Smartphone, Save, AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { adminAxios } from "../../context/AdminAuth";
import { AppleLogo, PlayLogo } from "../../components/landing/StoreBadges";

const VERSION_RE = /^\d+(\.\d+){0,3}$/;
const URL_RE = /^https:\/\/\S+$/;
const EMPTY = { ios_version: "", android_version: "", ios_url: "", android_url: "" };

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(", ");
  return d || "Something went wrong. Please try again.";
};

const fmtDate = (iso) => {
  if (!iso) return "Never";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

const Field = ({ label, value, onChange, placeholder, error, testId }) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#8AA583]">{label}</span>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`mt-2 w-full rounded-xl border bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#2F5E43]/30 ${
        error ? "border-red-300" : "border-[#E8EBE9]"
      }`}
      data-testid={testId}
    />
    {error && <span className="mt-1 block text-xs text-red-600" data-testid={`${testId}-error`}>{error}</span>}
  </label>
);

const PlatformCard = ({ icon, title, current, version, url, onVersion, onUrl, errors, prefix }) => (
  <div className="rounded-2xl border border-[#E8EBE9] bg-[#FBFBF9] p-5" data-testid={`appversion-${prefix}-card`}>
    <div className="flex items-center gap-2 text-[#111E16]">
      {icon}
      <h3 className="font-heading font-semibold">{title}</h3>
      <span className="ml-auto text-sm text-[#4A5F52]">
        Published: <span className="font-semibold text-[#2F5E43]" data-testid={`appversion-${prefix}-current`}>{current || "Not set"}</span>
      </span>
    </div>
    <div className="mt-4 space-y-4">
      <Field label="Version number" value={version} onChange={onVersion} placeholder="1.0.0" error={errors.version} testId={`appversion-${prefix}-version-input`} />
      <Field label="Store URL (optional)" value={url} onChange={onUrl} placeholder="https://" error={errors.url} testId={`appversion-${prefix}-url-input`} />
    </div>
  </div>
);

const validate = (f) => {
  const e = { ios: {}, android: {} };
  if (!VERSION_RE.test(f.ios_version)) e.ios.version = "Use numbers and dots only, like 1.2.3";
  if (!VERSION_RE.test(f.android_version)) e.android.version = "Use numbers and dots only, like 1.2.3";
  if (f.ios_url && !URL_RE.test(f.ios_url)) e.ios.url = "Must be a full https:// link";
  if (f.android_url && !URL_RE.test(f.android_url)) e.android.url = "Must be a full https:// link";
  return e;
};

export const AppUpdatesTab = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);

  const apply = (d) => {
    setData(d);
    setForm({
      ios_version: d.ios_version || "",
      android_version: d.android_version || "",
      ios_url: d.ios_url || "",
      android_url: d.android_url || "",
    });
  };

  const load = () => {
    setLoading(true);
    setError("");
    adminAxios.get("/admin/app-version").then((r) => apply(r.data)).catch((e) => setError(errText(e))).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const errors = validate(form);
  const invalid = Object.keys(errors.ios).length + Object.keys(errors.android).length > 0;
  const set = (k) => (v) => { setForm((f) => ({ ...f, [k]: v.trim() })); setNotice(null); };

  const save = async () => {
    if (invalid) return;
    const body = { ios_version: form.ios_version, android_version: form.android_version };
    if (form.ios_url && form.ios_url !== (data?.ios_url || "")) body.ios_url = form.ios_url;
    if (form.android_url && form.android_url !== (data?.android_url || "")) body.android_url = form.android_url;
    setSaving(true);
    try {
      const r = await adminAxios.put("/admin/app-version", body);
      if (r.data && r.data.ios_version) apply(r.data);
      else load();
      setNotice({ ok: true, text: "App versions saved." });
      toast.success("App versions saved");
    } catch (e) {
      setNotice({ ok: false, text: errText(e) });
      toast.error("Could not save app versions");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16" data-testid="appversion-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#2F5E43]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-start gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-6" data-testid="appversion-unavailable">
        <div className="flex items-start gap-3 text-amber-800">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <p className="text-sm" data-testid="appversion-unavailable-message">{error}</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-white px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100" data-testid="appversion-retry-btn">
          <RefreshCw className="h-4 w-4" /> Try again
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-[#2F5E43]/10 bg-white p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)]" data-testid="appversion-panel">
      <div className="flex flex-wrap items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#E4EDE6] text-[#2F5E43]"><Smartphone className="h-4 w-4" /></span>
        <h2 className="font-heading text-lg font-semibold text-[#111E16]">App Updates</h2>
        <span className="ml-auto text-sm text-[#4A5F52]" data-testid="appversion-updated">
          Last updated {fmtDate(data?.updated_at)}{data?.updated_by ? ` by ${data.updated_by}` : ""}
        </span>
      </div>
      <p className="mt-2 text-sm text-[#4A5F52]">
        Enter the versions already published in the stores. Updates are optional for users; the app shows its own update prompt.
      </p>
      <div className="mt-6 grid gap-5 md:grid-cols-2">
        <PlatformCard icon={<AppleLogo className="h-5 w-5" />} title="iOS" prefix="ios" current={data?.ios_version}
          version={form.ios_version} url={form.ios_url} onVersion={set("ios_version")} onUrl={set("ios_url")} errors={errors.ios} />
        <PlatformCard icon={<PlayLogo className="h-5 w-5" />} title="Android" prefix="android" current={data?.android_version}
          version={form.android_version} url={form.android_url} onVersion={set("android_version")} onUrl={set("android_url")} errors={errors.android} />
      </div>
      <div className="mt-6 flex flex-wrap items-center gap-4">
        <button onClick={save} disabled={saving || invalid}
          className="inline-flex items-center gap-2 rounded-full bg-[#2F5E43] px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#244A34] disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="appversion-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
        </button>
        {notice && (
          <span className={`inline-flex items-center gap-2 text-sm ${notice.ok ? "text-[#2F5E43]" : "text-red-600"}`} data-testid={notice.ok ? "appversion-success" : "appversion-error"}>
            {notice.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />} {notice.text}
          </span>
        )}
      </div>
    </div>
  );
};
